import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

/**
 * <oppie-cart-upsell>, upsell in de winkelwagen (lade en /cart).
 *
 * Twee lagen, allebei uit de catalogus-JSON die de snippet meegeeft:
 *
 * 1. Gratis tegoed. Zit er een product met een gratis-kiezer in de wagen
 *    (bijv. de broodbakmachine: 3 broodmixen gratis) en zijn er minder gratis
 *    stuks gekozen dan het tegoed, dan staat er een roze kaart "Nog N gratis te
 *    kiezen" met de leverbare opties en een plus-knop.
 * 2. Add-ons. Producten met dezelfde `upsell:<groep>`-tag als iets in de wagen,
 *    nog niet in de wagen, leverbaar, niet getagd oppie-geen-upsell, en niet
 *    duurder dan `data-max-ratio` keer de prijs van het groepsproduct in de
 *    wagen. Zo komt een apparaat nooit als add-on onder een zakje mix. Geen
 *    groepsmatch? Dan alleen de handmatige fallback-collectie uit de
 *    theme-instellingen, verder niets: liever geen upsell dan een onlogische.
 *
 * Lege wagen: de fallback-collectie, anders een brede greep uit de catalogus.
 * Toevoegen loopt via /cart/add.js plus CartAddEvent, zodat lade, verzendbalk
 * en totalen via Horizon's section-morph meebewegen.
 */

const GIFT_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>';
const PLUS_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>';
/**
 * Laatste cart zoals een cart:update-event hem meegaf (event.detail.resource).
 * Zo hoeft de rail na een wijziging geen /cart.js meer op te halen: de lade
 * is dan al gemorpht en de kaarten staan er nog (data-skip-subtree-update).
 */
let lastEventCart = null;

const isCart = (value) => Boolean(value && typeof value === 'object' && Array.isArray(value.items));

class OppieCartUpsell extends HTMLElement {
  #scheduled = false;
  #lastKey = null;
  #renderId = 0;
  #cart = null;

  connectedCallback() {
    this.list = this.querySelector('[data-upsell-list]');
    this.nav = this.querySelector('[data-upsell-nav]');
    this.rail = this.querySelector('[data-upsell-rail]');
    this.freeBox = this.querySelector('[data-upsell-free]');
    this.maxRatio = parseFloat(this.dataset.maxRatio) || 4;
    this.freeTitle = this.dataset.freeTitle || '';
    this.freeSub = this.dataset.freeSub || '';
    this.freeLabel = this.dataset.freeLabel || '';
    this.status = this.querySelector('[data-upsell-status]');
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.addUrl = this.dataset.addUrl || '/cart/add';
    this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    this.currency = this.dataset.currency || 'EUR';
    this.addLabel = this.dataset.addLabel || 'Add';
    this.allowEmpty = this.hasAttribute('data-allow-empty');
    this.ratingLabel = this.dataset.ratingLabel || '';
    this.ratings = this.#readRatings();
    this.fallback = this.#readFallback('[data-oppie-upsell-fallback]');
    this.broadFallback = this.#readFallback('[data-oppie-upsell-broad-fallback]');
    this.catalog = this.#readFallback('[data-oppie-upsell-catalog]');
    this.byId = new Map(this.catalog.map((p) => [p.id, p]));

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#bindNav();
    // Lege-wagen-variant (lege lade, lege /cart, of het template dat
    // component-cart-items.js kloont zodra de laatste regel verdwijnt): de
    // wagen is leeg of wordt het, dus direct de fallback tekenen. Anders de
    // cart uit het laatste event; alleen bij de eerste paginaweergave is er
    // nog geen event en halen we /cart.js op.
    if (this.allowEmpty) {
      this.#renderEmpty();
    } else {
      this.render(lastEventCart);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Het event wordt verstuurd vóór de morph van de lade (component-cart-items.js,
   * product-form.js). Een microtask draait daarna, maar nog vóór de browser
   * tekent: de kaarten worden dus in één keer bijgewerkt, zonder tussenbeeld.
   */
  #onCartUpdate = (event) => {
    const cart = event?.detail?.resource;
    if (isCart(cart)) lastEventCart = cart;
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      if (!this.isConnected) return;
      this.render(lastEventCart);
    });
  };

  /**
   * Beoordelingen per product-id, geleverd door de Liquid-snippet. De
   * Recommendations-API kent de review-metavelden niet, dus zoeken we ze hier op.
   */
  /**
   * De JSON-blokken staan één keer op de pagina, buiten de lade
   * (snippets/oppie-cart-upsell-data.liquid via sections/oppie-header.liquid).
   * Een rail met eigen blokken (oudere markup) blijft werken.
   */
  #dataNode(selector) {
    return this.querySelector(selector) || document.querySelector(`[data-oppie-upsell-data] ${selector}`);
  }

  #readRatings() {
    const node = this.#dataNode('[data-oppie-upsell-ratings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch {
      return {};
    }
  }

  #readFallback(selector) {
    const node = this.#dataNode(selector);
    if (!node) return [];
    try {
      return JSON.parse(node.textContent).map((p) => this.#normalize(p));
    } catch {
      return [];
    }
  }

  /**
   * Normalises both /recommendations/products.json products and the embedded
   * fallback JSON into one shape.
   */
  #normalize(p) {
    const image = p.image || p.featured_image || (Array.isArray(p.images) ? p.images[0] : null);
    const variants = (p.variants || []).map((v) => ({
      id: v.id,
      title: v.title,
      available: Boolean(v.available),
      price: v.price,
    }));
    return {
      id: p.id,
      handle: p.handle || '',
      title: p.title,
      url: p.url,
      image,
      price: p.price,
      compareAt: p.compare_at_price && p.compare_at_price > p.price ? p.compare_at_price : 0,
      available: Boolean(p.available),
      variants,
      groups: Array.isArray(p.groups) ? p.groups : [],
      noUpsell: Boolean(p.no_upsell),
      free: p.free && Array.isArray(p.free.ids) ? { qty: Number(p.free.qty) || 0, ids: p.free.ids.map(Number) } : null,
    };
  }

  async render(knownCart = null) {
    const token = ++this.#renderId;
    let cart = isCart(knownCart) ? knownCart : null;
    if (!cart) {
      try {
        cart = await (await fetch(`${Theme.routes.cart_url || '/cart'}.js`)).json();
      } catch {
        cart = null;
      }
      if (token !== this.#renderId) return; // een nieuwere render is al onderweg
      if (isCart(cart)) lastEventCart = cart;
    }
    const isEmpty = !cart || cart.item_count === 0;
    if (isEmpty) {
      // Empty-cart state: only render when explicitly opted in (empty-cart
      // template / empty branch). Show fallback recommendations so the cart
      // isn't a dead end; there's no seed product to fetch complementary items.
      if (!this.allowEmpty) {
        this.hidden = true;
        return;
      }
      this.#renderEmpty();
      return;
    }

    this.#cart = cart;
    const inCartQty = new Map();
    for (const item of cart.items) {
      inCartQty.set(item.product_id, (inCartQty.get(item.product_id) || 0) + item.quantity);
    }
    const key = [...inCartQty.entries()].sort((a, b) => a[0] - b[0]).map(([id, qty]) => `${id}:${qty}`).join(',');
    if (key === this.#lastKey && (this.list.querySelector('.oppie-upsell__card') || this.freeBox?.childElementCount)) return;
    this.#lastKey = key;

    const freeBlocks = this.#freeBlocks(cart, inCartQty);
    const freeIds = new Set(freeBlocks.flatMap((b) => b.options.map((o) => o.id)));

    let products = this.#groupCandidates(cart, inCartQty).filter((p) => !freeIds.has(p.id));
    if (products.length === 0) {
      products = this.fallback
        .filter((p) => !inCartQty.has(p.id) && p.available && !p.noUpsell)
        .map((p) => ({ ...p, upsellSource: 'wagen-fallback', upsellHost: '' }));
    }
    products = products.slice(0, this.limit);

    this.#paintFree(freeBlocks);
    if (this.rail) this.rail.hidden = products.length === 0;
    if (products.length) this.#paint(products);
    else this.list.replaceChildren();

    this.hidden = products.length === 0 && freeBlocks.length === 0;
  }

  /**
   * Gratis tegoed per product met een gratis-kiezer in de wagen: hoeveel
   * gratis stuks er nog te kiezen zijn, en welke opties leverbaar zijn.
   */
  #freeBlocks(cart, inCartQty) {
    const blocks = [];
    for (const [productId, qty] of inCartQty) {
      const host = this.byId.get(productId);
      if (!host || !host.free || host.free.qty <= 0) continue;
      const total = host.free.qty * qty;
      const picked = host.free.ids.reduce((sum, id) => sum + (inCartQty.get(id) || 0), 0);
      const left = total - picked;
      if (left <= 0) continue;
      const options = host.free.ids.map((id) => this.byId.get(id)).filter((p) => p && p.available);
      if (options.length) blocks.push({ host, left, options });
    }
    return blocks;
  }

  /**
   * Add-ons uit dezelfde upsell-groep als iets in de wagen, met een prijsplafond
   * per groep zodat een apparaat nooit als add-on onder een consumable staat.
   */
  #groupCandidates(cart, inCartQty) {
    const groupMax = new Map();
    for (const item of cart.items) {
      const p = this.byId.get(item.product_id);
      if (!p) continue;
      for (const g of p.groups) {
        groupMax.set(g, Math.max(groupMax.get(g) || 0, item.price ?? p.price));
      }
    }
    if (groupMax.size === 0) return [];
    const groupHost = new Map();
    for (const item of cart.items) {
      const p = this.byId.get(item.product_id);
      if (!p) continue;
      for (const g of p.groups) {
        if ((item.price ?? p.price) >= (groupMax.get(g) || 0)) groupHost.set(g, p.handle);
      }
    }
    return this.catalog
      .filter((p) => {
        if (inCartQty.has(p.id) || !p.available || p.noUpsell) return false;
        return p.groups.some((g) => groupMax.has(g) && p.price <= groupMax.get(g) * this.maxRatio);
      })
      .map((p) => {
        const g = p.groups.find((x) => groupMax.has(x));
        return { ...p, upsellSource: 'wagen-addon', upsellHost: groupHost.get(g) || '' };
      });
  }

  #paintFree(blocks) {
    if (!this.freeBox) return;
    this.freeBox.replaceChildren();
    this.freeBox.hidden = blocks.length === 0;
    for (const block of blocks) {
      const card = document.createElement('div');
      card.className = 'oppie-upsell__free-card';

      const head = document.createElement('div');
      head.className = 'oppie-upsell__free-head';
      const chip = document.createElement('span');
      chip.className = 'oppie-upsell__free-chip';
      chip.innerHTML = GIFT_ICON;
      head.appendChild(chip);
      const titles = document.createElement('div');
      titles.className = 'oppie-upsell__free-titles';
      const title = document.createElement('span');
      title.className = 'oppie-upsell__free-title';
      title.textContent = this.freeTitle.replace('__COUNT__', String(block.left));
      titles.appendChild(title);
      const sub = document.createElement('span');
      sub.className = 'oppie-upsell__free-sub';
      sub.textContent = this.freeSub.replace('__PRODUCT__', block.host.title);
      titles.appendChild(sub);
      head.appendChild(titles);
      card.appendChild(head);

      const rows = document.createElement('ul');
      rows.className = 'oppie-upsell__free-rows';
      rows.setAttribute('role', 'list');
      for (const option of block.options) {
        const variant = option.variants.find((v) => v.available) || option.variants[0];
        const row = document.createElement('li');
        row.className = 'oppie-upsell__free-row';
        if (option.image) {
          const img = document.createElement('img');
          img.className = 'oppie-upsell__free-thumb';
          img.src = this.#imageUrl(option.image, 88);
          img.alt = '';
          img.width = 44;
          img.height = 44;
          img.loading = 'lazy';
          row.appendChild(img);
        }
        const info = document.createElement('div');
        info.className = 'oppie-upsell__free-info';
        const name = document.createElement('a');
        name.className = 'oppie-upsell__free-name';
        name.href = option.url;
        name.textContent = option.title;
        info.appendChild(name);
        const price = document.createElement('span');
        price.className = 'oppie-upsell__free-price';
        price.textContent = this.freeLabel;
        info.appendChild(price);
        row.appendChild(info);

        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'oppie-upsell__free-add';
        add.innerHTML = PLUS_ICON;
        const addText = document.createElement('span');
        addText.textContent = this.addLabel;
        add.appendChild(addText);
        add.setAttribute('aria-label', `${this.addLabel}: ${option.title}`);
        if (!variant || !variant.available) add.disabled = true;
        add.addEventListener('click', () => {
          if (variant) this.#add(variant.id, add, this.#freeProperties(variant.id, block.host.handle));
        });
        row.appendChild(add);
        rows.appendChild(row);
      }
      card.appendChild(rows);
      this.freeBox.appendChild(card);
    }
  }

  /**
   * Staat deze mix al in de wagen, dan dezelfde regeleigenschappen meegeven:
   * Shopify voegt de stuks dan bij die regel, anders staat dezelfde mix er
   * twee keer in (bijv. "pdp-gratis" naast "wagen-gratis").
   */
  #freeProperties(variantId, hostHandle) {
    const existing = this.#cart?.items?.find((item) => item.variant_id === Number(variantId));
    if (existing) return { ...(existing.properties || {}) };
    return { _oppie_upsell: 'wagen-gratis', _oppie_upsell_bij: hostHandle };
  }

  /**
   * Empty-cart recommendations. No seed product exists, so fall straight through
   * to the curated fallback (and the broad fallback when that's empty).
   */
  #renderEmpty() {
    if (this.#lastKey === 'empty' && this.list.querySelector('.oppie-upsell__card')) return;

    let products = this.fallback.filter((p) => p.available && !p.noUpsell);
    if (products.length === 0) products = this.broadFallback.filter((p) => p.available && !p.noUpsell);
    products = products.slice(0, this.limit).map((p) => ({ ...p, upsellSource: 'wagen-leeg', upsellHost: '' }));

    if (products.length === 0) {
      this.hidden = true;
      return;
    }

    this.#lastKey = 'empty';
    this.hidden = false;
    if (this.freeBox) {
      this.freeBox.hidden = true;
      this.freeBox.replaceChildren();
    }
    if (this.rail) this.rail.hidden = false;
    this.#paint(products);
  }

  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  #imageUrl(url, width) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${width}`;
  }

  #starSvg() {
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'oppie-upsell__star');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const poly = document.createElementNS(NS, 'polygon');
    poly.setAttribute('points', '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2');
    svg.appendChild(poly);
    return svg;
  }

  #starRow(className, count) {
    const row = document.createElement('span');
    row.className = className;
    row.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < count; i += 1) row.appendChild(this.#starSvg());
    return row;
  }

  /**
   * Sterren plus aantal recensies. Vult op de exacte score, niet afgerond naar
   * hele sterren, net als de productkaarten elders op de site. Geeft null terug
   * als er nog geen reviews zijn, zodat een kaart nooit nul sterren toont.
   */
  #rating(productId) {
    const data = this.ratings[String(productId)];
    if (!data || !data.count || !data.rating) return null;

    const max = Number(data.max) || 5;
    const starCount = Math.round(max);
    const fillPct = Math.max(0, Math.min(100, (Number(data.rating) / max) * 100));

    const wrap = document.createElement('div');
    wrap.className = 'oppie-upsell__rating';

    const stars = document.createElement('span');
    stars.className = 'oppie-upsell__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute(
      'aria-label',
      this.ratingLabel.replace('__RATING__', String(Math.round(Number(data.rating) * 10) / 10))
    );
    stars.appendChild(this.#starRow('oppie-upsell__stars-track', starCount));

    const fill = this.#starRow('oppie-upsell__stars-fill', starCount);
    fill.style.width = `${fillPct}%`;
    stars.appendChild(fill);
    wrap.appendChild(stars);

    const count = document.createElement('span');
    count.className = 'oppie-upsell__rating-count';
    count.textContent = `(${data.count})`;
    wrap.appendChild(count);

    return wrap;
  }

  #paint(products) {
    this.list.replaceChildren(...products.map((p) => this.#card(p)));
    this.#updateNav();
  }

  #card(product) {
    const card = document.createElement('div');
    card.className = 'oppie-upsell__card';

    const top = document.createElement('div');
    top.className = 'oppie-upsell__card-top';

    if (product.image) {
      const img = document.createElement('img');
      img.className = 'oppie-upsell__thumb';
      img.src = this.#imageUrl(product.image, 120);
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 56;
      img.height = 56;
      top.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'oppie-upsell__info';

    const name = document.createElement('a');
    name.className = 'oppie-upsell__name';
    name.href = product.url;
    name.textContent = product.title;
    info.appendChild(name);

    const rating = this.#rating(product.id);
    if (rating) info.appendChild(rating);

    const price = document.createElement('span');
    price.className = 'oppie-upsell__price';
    this.#renderPrice(price, product.price, product.compareAt);
    info.appendChild(price);

    top.appendChild(info);
    card.appendChild(top);

    const actions = document.createElement('div');
    actions.className = 'oppie-upsell__actions';

    const sellable = product.variants.filter((v) => v.available);
    let currentVariant = sellable[0] || product.variants[0];

    if (sellable.length > 1) {
      const select = document.createElement('select');
      select.className = 'oppie-upsell__select';
      for (const v of sellable) {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title;
        select.appendChild(opt);
      }
      select.addEventListener('change', () => {
        const v = sellable.find((x) => String(x.id) === select.value);
        if (v) {
          currentVariant = v;
          this.#renderPrice(price, v.price, product.compareAt);
        }
      });
      actions.appendChild(select);
    }

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'oppie-upsell__add';
    add.textContent = this.addLabel;
    add.setAttribute('aria-label', `${this.addLabel}: ${product.title}`);
    if (!currentVariant || !currentVariant.available) add.disabled = true;
    add.addEventListener('click', () => {
      if (currentVariant) {
        this.#add(currentVariant.id, add, {
          _oppie_upsell: product.upsellSource || 'wagen-addon',
          _oppie_upsell_bij: product.upsellHost || '',
        });
      }
    });
    actions.appendChild(add);

    card.appendChild(actions);
    return card;
  }

  #renderPrice(node, price, compareAt) {
    node.replaceChildren();
    const onSale = Boolean(compareAt && compareAt > price);
    node.classList.toggle('oppie-upsell__price--sale', onSale);

    if (onSale) {
      const was = document.createElement('s');
      was.textContent = this.#money(compareAt);
      node.appendChild(was);
    }

    const now = document.createElement('span');
    now.className = 'oppie-upsell__price-now';
    now.textContent = this.#money(price);
    node.appendChild(now);
  }

  /**
   * Voegt één stuk toe, met verborgen regeleigenschappen die in de order
   * laten zien dat het via de winkelwagen-upsell kwam (rapport:
   * scripts/upsell_rapport.py).
   */
  async #add(variantId, button, properties = {}) {
    button.disabled = true;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1, properties }],
      sections: sectionIds.join(','),
    });

    try {
      const response = await fetch(this.addUrl, fetchConfig('json', { body }));
      const result = await response.json();

      if (result.status) {
        this.#setStatus(result.description || result.message || '');
        button.disabled = false;
        return;
      }
      this.#setStatus('');

      let cart = null;
      try {
        cart = await (await fetch(`${Theme.routes.cart_url || '/cart'}.js`)).json();
      } catch {
        /* non-blocking */
      }

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id || 'oppie-cart-upsell', {
          source: 'oppie-cart-upsell',
          productId: undefined,
          variantId: String(variantId),
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error('Oppie cart upsell add failed:', error);
      button.disabled = false;
    }
  }

  #setStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
  }

  #bindNav() {
    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    const step = () => Math.max(180, Math.round(this.list.clientWidth * 0.8));
    prev?.addEventListener('click', () => this.list.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => this.list.scrollBy({ left: step(), behavior: 'smooth' }));
    this.list?.addEventListener('scroll', () => this.#updateNav(), { passive: true });
  }

  #updateNav() {
    if (!this.nav || !this.list) return;
    const overflow = this.list.scrollWidth - this.list.clientWidth > 4;
    this.nav.hidden = !overflow;
    if (!overflow) return;

    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    const atStart = this.list.scrollLeft <= 2;
    const atEnd = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 2;
    if (prev) prev.disabled = atStart;
    if (next) next.disabled = atEnd;
  }
}

if (!customElements.get('oppie-cart-upsell')) {
  customElements.define('oppie-cart-upsell', OppieCartUpsell);
}
